/**
 * Vendor/event detail page (follow-up to backbonecraftco-com#23's vendor
 * calendar). Reads the CraftCore Market Event identifier from this page's
 * own `?event=` query param, fetches its public detail from
 * backbone-craftcore-erpnext's guest-readable RPC
 * (craftcore.market_calendar.get_event_detail), and renders it. See
 * craftcore/market_calendar.py in that repo for the exact response shape.
 *
 * Cross-origin fetch, same pattern as assets/vendor-calendar.js -- see
 * that file's header comment for the full CORS/guest-RPC rationale.
 */

import {
  formatDateLong,
  formatTimeRange,
  formatLocation,
  buildDirectionsUrl,
  buildDescriptionFragment,
  buildIcsContent,
  buildGoogleCalendarUrl,
} from '@theme/vendor-event-format';

const API_URL = 'https://erp.backbonecraftco.com/api/method/craftcore.market_calendar.get_event_detail';
const UPCOMING_EVENTS_API_URL = 'https://erp.backbonecraftco.com/api/method/craftcore.market_calendar.get_upcoming_events';

/** Triggers an in-browser download of `event`'s .ics content -- no server
 * round-trip, matching the rest of this page's guest-RPC-only data flow.
 * The Blob/anchor-click pattern is the standard way to save
 * client-generated file content since `<a download>` requires a same-
 * origin or `data:`/`blob:` URL, and `text/calendar` content this size
 * comfortably fits in a Blob URL. */
function downloadIcsFile(event) {
  const filename = `${(event.market_name || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}.ics`;
  const blob = new Blob([buildIcsContent(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Trimmed string, or `null` when the value is missing or blank. */
function nonBlank(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/** Returns an absolute http(s) URL for a staff-entered link value, or `null`
 * when it is blank or unusable. A bare domain (e.g. `facebook.com/page`) gets
 * `https://` prepended. Anything else is forced under `https://` by the
 * prefix, so a value like `javascript:...` can never become an `href`. */
function toSafeHttpUrl(value) {
  const text = nonBlank(value);
  if (!text) return null;
  const candidate = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** Points `linkEl` at `url`, or hides it when there is no usable URL. */
function setOptionalLink(linkEl, url) {
  if (!linkEl) return;
  linkEl.hidden = !url;
  if (url) linkEl.href = url;
}

/** Facebook share-dialog URL for `pageUrl`. No API or auth is involved; the
 * page URL is percent-encoded into the `u` query param. */
function buildFacebookShareUrl(pageUrl) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
}

/** Icon suffix for a link's `platform`, keyed by the `platform` value on each
 * `market_links` / `venue_links` entry. The suffix becomes the modifier class
 * `vendor-event-detail__link-icon--<suffix>`, whose SVG mask is defined in
 * sections/vendor-event-detail.liquid. Unknown or missing platforms fall back
 * to "other". */
const PLATFORM_ICON_SUFFIXES = {
  Website: 'website',
  Facebook: 'facebook',
  Instagram: 'instagram',
  X: 'x',
  TikTok: 'tiktok',
  Other: 'other',
};

/** Modifier class for a link's icon, falling back to "other" for anything unknown. */
function platformIconClass(platform) {
  const suffix = Object.hasOwn(PLATFORM_ICON_SUFFIXES, platform)
    ? PLATFORM_ICON_SUFFIXES[platform]
    : PLATFORM_ICON_SUFFIXES.Other;
  return `vendor-event-detail__link-icon--${suffix}`;
}

/** Builds one link from a `market_links` or `venue_links` entry (both share
 * the `{label, url, platform}` shape), or returns `null` when its URL is
 * unusable so that entry is skipped. The platform icon is an empty, aria-hidden
 * span whose SVG comes from its modifier class (see platformIconClass), followed
 * by the label as a text node. Built via DOM methods, never `innerHTML`. */
function buildMarketLink(link) {
  const url = toSafeHttpUrl(link?.url);
  if (!url) return null;
  const anchor = document.createElement('a');
  anchor.className = 'vendor-event-detail__text-link';
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';

  const icon = document.createElement('span');
  icon.className = `vendor-event-detail__link-icon ${platformIconClass(link?.platform)}`;
  icon.setAttribute('aria-hidden', 'true');

  anchor.append(icon, document.createTextNode(` ${nonBlank(link?.label) ?? 'Link'}`));
  return anchor;
}

/** Link elements for a `market_links` / `venue_links` list, minus the
 * `Website` entries. Those duplicate the name link the host and venue blocks
 * already render, so only the social platforms show in the link rows. */
function buildSecondaryLinkEls(links) {
  const entries = Array.isArray(links) ? links : [];
  return entries
    .filter((link) => link?.platform !== 'Website')
    .map(buildMarketLink)
    .filter(Boolean);
}

/** Usable URL for the venue name in the address line: the first `Website`
 * entry in `venue_links` that has a usable URL, otherwise the first entry
 * with a usable URL, otherwise `null` (venue name renders as plain text). */
function pickVenueUrl(venueLinks) {
  const usable = (Array.isArray(venueLinks) ? venueLinks : [])
    .map((link) => ({ platform: link?.platform, url: toSafeHttpUrl(link?.url) }))
    .filter((entry) => entry.url);
  const website = usable.find((entry) => entry.platform === 'Website');
  return (website ?? usable[0])?.url ?? null;
}

/** A plain text node, or an anchor around `text` when `url` is set. The
 * address-line link uses its own class so the venue-links row's right
 * margin does not apply mid-sentence. */
function buildTextOrLink(text, url) {
  if (!url) return document.createTextNode(text);
  const anchor = document.createElement('a');
  anchor.className = 'vendor-event-detail__address-link';
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.textContent = text;
  return anchor;
}

class VendorEventDetail {
  constructor(root) {
    this.root = root;
    this.statusEl = root.querySelector('[data-vendor-event-detail-status]');
    this.contentEl = root.querySelector('[data-vendor-event-detail-content]');
    this.headingEl = root.querySelector('[data-vendor-event-detail-heading]');
    this.dateEl = root.querySelector('[data-vendor-event-detail-date]');
    this.timeEl = root.querySelector('[data-vendor-event-detail-time]');
    this.addressEl = root.querySelector('[data-vendor-event-detail-address]');
    this.actionsEl = root.querySelector('[data-vendor-event-detail-actions]');
    this.directionsEl = root.querySelector('[data-vendor-event-detail-directions]');
    this.googleCalendarEl = root.querySelector('[data-vendor-event-detail-google-calendar]');
    this.facebookShareEl = root.querySelector('[data-vendor-event-detail-facebook-share]');
    this.icsButtonEl = root.querySelector('[data-vendor-event-detail-ics]');
    this.descriptionEl = root.querySelector('[data-vendor-event-detail-description]');
    this.hostEl = root.querySelector('[data-vendor-event-detail-host]');
    this.hostLinkEl = root.querySelector('[data-vendor-event-detail-host-link]');
    this.hostNameEl = root.querySelector('[data-vendor-event-detail-host-name]');
    this.hostLinksEl = root.querySelector('[data-vendor-event-detail-host-links]');
    this.venueEl = root.querySelector('[data-vendor-event-detail-venue]');
    this.venueNameEl = root.querySelector('[data-vendor-event-detail-venue-name]');
    this.venueNameLinkEl = root.querySelector('[data-vendor-event-detail-venue-name-link]');
    this.venueNameTextEl = root.querySelector('[data-vendor-event-detail-venue-name-text]');
    this.venuePhoneEl = root.querySelector('[data-vendor-event-detail-venue-phone]');
    this.venueLinksEl = root.querySelector('[data-vendor-event-detail-venue-links]');

    this.currentEvent = null;
    this.icsButtonEl?.addEventListener('click', () => {
      if (this.currentEvent) downloadIcsFile(this.currentEvent);
    });

    this.eventName = new URLSearchParams(window.location.search).get('event');
    this.loadEvent();
  }

  async loadEvent() {
    if (!this.eventName) {
      await this.loadNearestUpcomingEvent();
      return;
    }

    try {
      const event = await this.fetchEventDetail(this.eventName);
      if (!event) {
        this.showNotFound();
        return;
      }
      this.renderEvent(event);
    } catch (error) {
      this.showError();
    }
  }

  /** Fallback path for a bare `/pages/vendor-events` visit (no `?event=`
   * at all): looks up the nearest upcoming event via the same
   * pre-filtered, pre-sorted `get_upcoming_events()` RPC the calendar
   * uses (already ordered by `start_date asc` server-side, see
   * craftcore/market_calendar.py), then converges on the same
   * fetch-and-render path as an explicit `?event=` visit once a name is
   * known. Does not change behavior for an explicit-but-invalid event id
   * -- that still falls through to showNotFound() in loadEvent() above. */
  async loadNearestUpcomingEvent() {
    try {
      const response = await fetch(UPCOMING_EVENTS_API_URL, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      const upcomingEvents = Array.isArray(data?.message) ? data.message : [];
      const nearest = upcomingEvents[0];
      if (!nearest?.name) {
        this.showNoUpcomingEvents();
        return;
      }

      const event = await this.fetchEventDetail(nearest.name);
      if (!event) {
        this.showNotFound();
        return;
      }

      this.eventName = nearest.name;
      this.updateEventQueryParam(nearest.name);
      this.renderEvent(event);
    } catch (error) {
      this.showError();
    }
  }

  /** Fetches a single event's public detail by name via
   * `get_event_detail()`, returning `null` (not throwing) when the RPC
   * responds successfully but has nothing for that id -- a network/HTTP
   * failure still throws so callers can distinguish "not found" from
   * "unable to load". */
  async fetchEventDetail(name) {
    const url = `${API_URL}?event=${encodeURIComponent(name)}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const data = await response.json();
    return data?.message ?? null;
  }

  /** Updates the address bar (not a navigation/reload) to include
   * `?event=<name>` once the nearest-upcoming fallback has resolved to a
   * specific event, so a refresh or share of the URL lands on that same
   * event, and so vendor-calendar.js's month-param back-button fix has a
   * consistent event id to reference if relevant. */
  updateEventQueryParam(name) {
    const url = new URL(window.location.href);
    url.searchParams.set('event', name);
    window.history.replaceState(window.history.state, '', url);
  }

  showStatus(state, message) {
    if (!this.statusEl) return;
    this.statusEl.hidden = false;
    this.statusEl.dataset.state = state;
    this.statusEl.textContent = message;
    this.contentEl && (this.contentEl.hidden = true);
  }

  showError() {
    this.showStatus('error', 'Unable to load this event right now. Please check back soon.');
  }

  showNotFound() {
    this.showStatus('not-found', "We couldn't find that event. It may have been removed or is no longer public.");
  }

  showNoUpcomingEvents() {
    this.showStatus('no-events', 'There are no upcoming events scheduled right now. Check back soon!');
  }

  renderEvent(event) {
    this.statusEl && (this.statusEl.hidden = true);
    this.contentEl && (this.contentEl.hidden = false);
    this.currentEvent = event;

    if (this.headingEl) this.headingEl.textContent = event.market_name || 'Event';
    if (this.dateEl) this.dateEl.textContent = formatDateLong(event);

    const timeRange = formatTimeRange(event);
    if (this.timeEl) {
      if (timeRange) {
        this.timeEl.textContent = timeRange;
        this.timeEl.hidden = false;
      } else {
        this.timeEl.hidden = true;
      }
    }

    const directionsUrl = buildDirectionsUrl({
      latitude: event.latitude,
      longitude: event.longitude,
      addressParts: [event.address_line, event.city, event.state, event.zip],
    });
    this.renderAddress(event, directionsUrl);

    if (this.directionsEl) {
      if (directionsUrl) {
        this.directionsEl.href = directionsUrl;
        this.directionsEl.hidden = false;
      } else {
        this.directionsEl.hidden = true;
      }
    }

    if (this.googleCalendarEl) this.googleCalendarEl.href = buildGoogleCalendarUrl(event);
    if (this.facebookShareEl) this.facebookShareEl.href = buildFacebookShareUrl(window.location.href);
    if (this.actionsEl) this.actionsEl.hidden = false;

    if (this.descriptionEl) {
      this.descriptionEl.replaceChildren(buildDescriptionFragment(event.public_description || 'Details coming soon.'));
    }

    this.renderHost(event);
    this.renderVenue(event);
  }

  /** Address line: "{venue name} - {address}". Each half becomes its own link
   * when it has a target: the venue name goes to the venue's website (see
   * pickVenueUrl), and the address goes to the same directions URL the Get
   * Directions button uses. A half with no target stays plain text, and a
   * missing half is dropped. The whole line hides when both halves are empty. */
  renderAddress(event, directionsUrl) {
    if (!this.addressEl) return;
    const venueName = nonBlank(event.venue_name);
    const location = formatLocation(event);
    if (!venueName && !location) {
      this.addressEl.replaceChildren();
      this.addressEl.hidden = true;
      return;
    }

    const halves = [];
    if (venueName) halves.push(buildTextOrLink(venueName, pickVenueUrl(event.venue_links)));
    if (location) halves.push(buildTextOrLink(location, directionsUrl));
    const nodes = halves.flatMap((node, index) => (index === 0 ? [node] : [document.createTextNode(' - '), node]));

    this.addressEl.replaceChildren(...nodes);
    this.addressEl.hidden = false;
  }

  /** "Hosted by" block. Hidden unless the market has a host name. The name
   * links to the host's website when one is set, otherwise it is plain text.
   * The block stays visible with no links when `market_links` is empty. */
  renderHost(event) {
    const hostName = nonBlank(event.host_name);
    if (this.hostEl) this.hostEl.hidden = !hostName;
    if (!hostName) return;

    const hostUrl = toSafeHttpUrl(event.host_website);
    if (this.hostLinkEl) this.hostLinkEl.textContent = hostName;
    if (this.hostNameEl) this.hostNameEl.textContent = hostName;
    setOptionalLink(this.hostLinkEl, hostUrl);
    if (this.hostNameEl) this.hostNameEl.hidden = Boolean(hostUrl);

    if (this.hostLinksEl) {
      const linkEls = buildSecondaryLinkEls(event.market_links);
      this.hostLinksEl.replaceChildren(...linkEls);
      this.hostLinksEl.hidden = linkEls.length === 0;
    }
  }

  /** Venue block: venue name, phone, and venue links (same `{label, url,
   * platform}` shape as the host's links). The address is deliberately not
   * repeated here, since it is already shown above next to Get Directions.
   * The whole block hides when none of the three have something to show.
   * The venue name links the same way as the host name, using pickVenueUrl
   * (the same target the address line uses). */
  renderVenue(event) {
    const venueName = nonBlank(event.venue_name);
    const phone = nonBlank(event.venue_phone);
    const venueUrl = venueName ? pickVenueUrl(event.venue_links) : null;
    const linkEls = buildSecondaryLinkEls(event.venue_links);
    if (this.venueEl) this.venueEl.hidden = !(venueName || phone || linkEls.length);

    if (this.venueNameEl) this.venueNameEl.hidden = !venueName;
    if (this.venueNameLinkEl) this.venueNameLinkEl.textContent = venueName ?? '';
    if (this.venueNameTextEl) this.venueNameTextEl.textContent = venueName ?? '';
    setOptionalLink(this.venueNameLinkEl, venueUrl);
    if (this.venueNameTextEl) this.venueNameTextEl.hidden = Boolean(venueUrl);
    if (this.venuePhoneEl) {
      this.venuePhoneEl.textContent = phone ?? '';
      this.venuePhoneEl.hidden = !phone;
    }
    if (this.venueLinksEl) {
      this.venueLinksEl.replaceChildren(...linkEls);
      this.venueLinksEl.hidden = linkEls.length === 0;
    }
  }
}

document.querySelectorAll('[data-vendor-event-detail]').forEach((root) => new VendorEventDetail(root));
